import { eraPhysics } from '../config/tuning';
import { physicsHash } from '../game/replay';
import type { DayMeta } from '../net/protocol';
export { metaPath } from '../net/protocol';
import { buildRuntime } from '../sim/trackQuery';
import { eraForDate } from './era';
import { type GeneratedCircuit, trackFingerprint } from './generate';
import { simHooksFor } from './modifiers';

/**
 * Circuits built ahead of time. Building a day's circuit takes a phone
 * seconds (the generator tries layouts and laps them with the bot), so every
 * deploy builds the coming days' circuits once (scripts/circuits.ts) and the
 * game downloads the day's file (~90 KB) instead. Nothing is taken on trust:
 * a file must fingerprint to the geometry it claims, with this code's car,
 * or the game builds the circuit itself as before.
 *
 * The format is JSON that keeps every number exact: -0 and non-finite values
 * (which JSON would turn into 0 and null) are tagged, as are typed arrays.
 */
export const PREBUILT_FORMAT = 1;

export function prebuiltPath(date: string): string {
  return `circuits/${date}.json`;
}

/** What the server needs to know about a day's circuit without building it. */
export function dayMeta(c: GeneratedCircuit): DayMeta {
  return { date: c.date, era: c.era, trackHash: c.trackHash, physicsHash: physicsHash(c.physics), authorMs: c.medals.author };
}

export function encodeCircuit(c: GeneratedCircuit): string {
  const { runtime: _runtime, ...rest } = c;
  return JSON.stringify({ format: PREBUILT_FORMAT, circuit: rest }, (_k, v: unknown) => {
    if (typeof v === 'number') {
      if (Object.is(v, -0)) return { $n: '-0' };
      if (!Number.isFinite(v)) return { $n: String(v) };
      return v;
    }
    if (ArrayBuffer.isView(v) && !(v instanceof DataView)) {
      return { $t: v.constructor.name, v: Array.from(v as unknown as ArrayLike<number>) };
    }
    return v;
  });
}

type TypedCtor = { from(v: ArrayLike<number>): unknown };
const TYPED: Record<string, TypedCtor> = { Float64Array, Float32Array, Int32Array, Uint32Array, Int16Array, Uint16Array, Int8Array, Uint8Array, Uint8ClampedArray };

/** A circuit from its file, checked; null if it isn't this code's circuit for that day. */
export function decodeCircuit(text: string): GeneratedCircuit | null {
  const o = JSON.parse(text, (_k, v: unknown) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const t = v as { $n?: string; $t?: string; v?: number[] };
      if (typeof t.$n === 'string' && Object.keys(t).length === 1) return Number(t.$n);
      if (typeof t.$t === 'string' && Array.isArray(t.v) && TYPED[t.$t]) return TYPED[t.$t].from(t.v);
    }
    return v;
  }) as { format?: number; circuit?: Omit<GeneratedCircuit, 'runtime'> };
  const c = o.circuit;
  if (o.format !== PREBUILT_FORMAT || !c?.track || !c.spec) return null;
  // The geometry it claims, and this code's car for the day (by this code's rotation): otherwise it's not ours to use.
  if (trackFingerprint(c.track) !== c.trackHash) return null;
  if (c.era !== eraForDate(c.date) || physicsHash(c.physics) !== physicsHash(eraPhysics(c.era, c.date))) return null;
  return { ...c, runtime: buildRuntime(c.track, simHooksFor(c.spec, c.track)) };
}

/** The day's prebuilt circuit, or null (none for that day, offline, or not matching this code). */
export async function fetchPrebuilt(date: string): Promise<GeneratedCircuit | null> {
  try {
    const r = await fetch(prebuiltPath(date));
    // A day without a file comes back as the page itself (the site's fallback), not JSON.
    if (!r.ok || !(r.headers.get('content-type') ?? '').includes('json')) return null;
    return decodeCircuit(await r.text());
  } catch {
    return null;
  }
}
